import { useState, useCallback, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import Loader       from './components/Loader';
import Cursor       from './components/Cursor';
import Navbar       from './components/Navbar';
import Hero         from './components/Hero';
import About        from './components/About';
import Skills       from './components/Skills';
import Projects     from './components/Projects';
import Experience   from './components/Experience';
import Tools        from './components/Tools';
import Certificates from './components/Certificates';
import Contact      from './components/Contact';
import Footer       from './components/Footer';
import { Divider }  from './components/utils';
import MarqueeTicker from './components/MarqueeTicker';
import { useTheme } from './hooks/useTheme'; // ← NEW
import Reviews from './components/reviews';
import Blog from './components/blog';

gsap.registerPlugin(ScrollTrigger);

// ── Assets the Loader will actually wait on ──────────────────────────────────
const CRITICAL_ASSETS = [
  '/miss.png','rock-bg.png',
  '/profile.png',
  '/profile2.png',
  '/Beg_python-1.png',
  '/data_structure-1.png',
  '/Dtsc-1.png',
  '/html-1.png',
  '/intermediate_python.png',
  '/js-1.png',
  '/pfl.png',
  '/PE-1.png',
  "/des.mp4",
  "vid_m.mp4",
  "about.mp4"
];

const getWidth = () => (typeof window !== 'undefined' ? window.innerWidth : 1024);

export default function App() {
  useTheme(); // ← NEW: fetches theme/colors from Firestore and injects into :root

  const [loaded, setLoaded] = useState(false);
  const handleLoaded = useCallback(() => setLoaded(true), []);

  const [isMobile, setIsMobile] = useState(() => getWidth() < 768);
  const [isTablet, setIsTablet] = useState(() => getWidth() < 1024);

  // ── Responsive breakpoint tracking ──────────────────────────────────────────
  useEffect(() => {
    const handle = () => {
      setIsMobile(getWidth() < 768);
      setIsTablet(getWidth() < 1024);
      if (loaded) ScrollTrigger.refresh();
    };
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, [loaded]);

  // ── Lenis smooth scroll – init ONLY after loader exits ──────────────────────
  useEffect(() => {
    if (!loaded) return;

    document.body.style.overflow = '';

    const initLenis = () => {
      if (!window.Lenis) return;
      const lenis = new window.Lenis({
        duration: 2.0,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: 'vertical',
        smoothWheel: true,
        wheelMultiplier: 0.6,
        touchMultiplier: 1.2,
      });
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add((time) => lenis.raf(time * 1000));
      gsap.ticker.lagSmoothing(0);
      ScrollTrigger.scrollerProxy(document.body, {
        scrollTop(value) {
          if (arguments.length) lenis.scrollTo(value, { immediate: true });
          return lenis.scroll;
        },
        getBoundingClientRect() {
          return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
        },
      });
      ScrollTrigger.addEventListener('refresh', () => lenis.resize());
      ScrollTrigger.refresh();
      window.__lenis = lenis;
    };

    if (window.Lenis) initLenis();
    else window.addEventListener('load', initLenis, { once: true });

    return () => {
      try { window.__lenis?.destroy(); } catch (_) {}
      delete window.__lenis;
    };
  }, [loaded]);

  // ── Global GSAP animations (progress bar + dividers) ────────────────────────
  useEffect(() => {
    if (!loaded) return;
    const ctx = gsap.context(() => {
      gsap.to('.page-progress', {
        scaleX: 1,
        ease: 'none',
        scrollTrigger: {
          trigger: document.body,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0,
        },
      });
      gsap.utils.toArray('.divider-line').forEach((el) => {
        gsap.fromTo(el, { scaleX: 0 }, {
          scaleX: 1,
          scrollTrigger: { trigger: el, start: 'top 85%', end: 'top 40%', scrub: 1 },
        });
      });
    });
    return () => ctx.revert();
  }, [loaded]);

  // ── Lock scroll while loader is active ──────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = loaded ? '' : 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [loaded]);

  return (
    <>
      {!loaded && (
        <Loader assets={CRITICAL_ASSETS} onComplete={handleLoaded} />
      )}

      <Cursor />
      <Navbar />

      <Hero         loaded={loaded} isMobile={isMobile} />

      {/* <MarqueeTicker loaded={loaded} isMobile={isMobile} /> */}

      <About        loaded={loaded} isMobile={isMobile} isTablet={isTablet} />

      <Skills       loaded={loaded} isMobile={isMobile} isTablet={isTablet} />
                  <Projects     loaded={loaded} isMobile={isMobile} />

      <Experience   loaded={loaded} isMobile={isMobile} />

            {/* <Projects     loaded={loaded} isMobile={isMobile} />
      <Divider color="var(--purple)" /> */}

      <Tools        loaded={loaded} isMobile={isMobile} />

      <Certificates loaded={loaded} isMobile={isMobile} />

      <Contact      loaded={loaded} isMobile={isMobile} />

      <Footer       loaded={loaded} />

      <style>{`
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.2} }
        @keyframes scrollPulse {
          0%  { transform:scaleY(0); transform-origin:top    }
          50% { transform:scaleY(1); transform-origin:top    }
          51% { transform:scaleY(1); transform-origin:bottom }
          100%{ transform:scaleY(0); transform-origin:bottom }
        }
        * { box-sizing: border-box; }
        @media (max-width:767px) { body { cursor: auto !important; } }
      `}</style>
    </>
  );
}
